// Authentication hook for managing user sessions
import { useState, useEffect, useCallback } from "react";
import { apiUrl } from "../config";

interface User {
  id: number;
  username: string;
  email?: string;
  is_admin: number;
  is_active: number;
  created_at: string;
  last_login?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  needsSetup: boolean; // True if no users exist yet
}

const TOKEN_KEY = "dishsnap_auth_token";

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    needsSetup: false,
  });

  // Check if token is valid on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);

      if (!storedToken) {
        // Check if any users exist (needs setup)
        try {
          const res = await fetch(apiUrl("/api/auth/me"));
          if (res.status === 401) {
            const data = await res.json();
            // If error is "Authentication required" with no users, needs setup
            setState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              token: null,
              needsSetup: false,
            });
          } else {
            setState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              token: null,
              needsSetup: false,
            });
          }
        } catch {
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            token: null,
            needsSetup: false,
          });
        }
        return;
      }

      try {
        const res = await fetch(apiUrl("/api/auth/me"), {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setState({
            isAuthenticated: true,
            isLoading: false,
            user: data.user,
            token: storedToken,
            needsSetup: false,
          });
        } else {
          // Token invalid, clear it
          localStorage.removeItem(TOKEN_KEY);
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            token: null,
            needsSetup: false,
          });
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
          needsSetup: false,
        });
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(apiUrl("/api/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: data.user,
          token: data.token,
          needsSetup: false,
        });
        return { success: true };
      } else {
        return { success: false, error: data.error || "Login failed" };
      }
    } catch (error) {
      return { success: false, error: "Network error" };
    }
  }, []);

  const register = useCallback(async (username: string, password: string, email?: string): Promise<{ success: boolean; error?: string; isFirstUser?: boolean }> => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(apiUrl("/api/auth/register"), {
        method: "POST",
        headers,
        body: JSON.stringify({ username, password, email }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        return { success: true, isFirstUser: data.isFirstUser };
      } else {
        return { success: false, error: data.error || "Registration failed" };
      }
    } catch (error) {
      return { success: false, error: "Network error" };
    }
  }, []);

  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (token) {
      try {
        await fetch(apiUrl("/api/auth/logout"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error("Logout error:", error);
      }
    }

    localStorage.removeItem(TOKEN_KEY);
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
      needsSetup: false,
    });
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (state.token) {
      headers.Authorization = `Bearer ${state.token}`;
    }
    return headers;
  }, [state.token]);

  return {
    ...state,
    login,
    register,
    logout,
    getAuthHeaders,
  };
}

// Helper to get token for API calls
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// Helper to add auth header to fetch options
export function withAuth(options: RequestInit = {}): RequestInit {
  const token = getAuthToken();
  if (!token) return options;

  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${token}`);

  return {
    ...options,
    headers,
  };
}
